import { Upload, Video, X, Headphones } from "lucide-react";
import AudioListen from "@/components/AudioListen";
import { CURRICULUM } from "@/lib/curriculum";

const ACCEPT = "video/mp4,video/quicktime,video/x-m4v,video/webm";

// Upload / Replace / Watch / Remove controls for one video slot (module id or "graduation").
export const VideoControls = ({ id, hasVideo, uploading, onUpload, onRemove }) => (
  <div className="flex flex-wrap items-center gap-2">
    <label className={`btn-outline text-xs cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid={`content-upload-${id}`}>
      <Upload size={12} />
      {uploading ? "Uploading…" : hasVideo ? "Replace video" : "Upload video"}
      <input type="file" accept={ACCEPT} className="hidden" disabled={uploading} onChange={(e) => onUpload(id, e.target.files?.[0])} />
    </label>
    {hasVideo && (
      <>
        <a href={`/modules/${id}/watch`} target="_blank" rel="noreferrer" className="btn-ember text-xs" data-testid={`content-watch-${id}`}>
          <Video size={12} /> Watch
        </a>
        <button onClick={() => onRemove(id)} className="btn-outline text-xs border-red-400/40 text-red-400 hover:border-red-400" data-testid={`content-remove-${id}`}>
          <X size={12} /> Remove
        </button>
      </>
    )}
  </div>
);

export const ModuleVideoManager = ({ available, uploadingId, onUpload, onRemove }) => (
  <>
    <div>
      <div className="section-tag mb-4 flex items-center gap-2"><Headphones size={12} className="text-[#B45309]" /> § The 8 Modules · Narrations + Full Video Uploads</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CURRICULUM.map((m) => {
          const hasVideo = available.has(m.id);
          return (
            <div key={m.id} className="tacc-card p-6" data-testid={`content-module-${m.id}`}>
              <div className="flex items-start gap-5">
                <span className="font-display text-3xl text-[#44403C] shrink-0 leading-none pt-1">{m.n}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-heading text-lg tracking-tight">{m.title}</h4>
                    {hasVideo && <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-emerald-400 border border-emerald-400/30 px-1.5 py-0.5">✓ Video</span>}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] mb-3">{m.duration} · {m.focus}</div>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-4">{m.snippet}</p>
                  <div className="mb-3">
                    <AudioListen text={m.narration} label="Play the narration" testid={`content-listen-${m.id}`} />
                  </div>
                  <div className="pt-3 border-t border-[#E7DFD5]">
                    <VideoControls id={m.id} hasVideo={hasVideo} uploading={uploadingId === m.id} onUpload={onUpload} onRemove={onRemove} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <div className="tacc-card p-6 md:p-8" data-testid="content-graduation">
      <div className="section-tag mb-4">§ Graduation video · plays when a member completes all 8 modules</div>
      <p className="text-[#78716C] text-sm mb-4 max-w-lg">
        {available.has("graduation")
          ? "Your graduation video is live. Members see it the moment they finish module 8."
          : "No video yet — members hear a narrated congratulations until you upload one."}
      </p>
      <VideoControls id="graduation" hasVideo={available.has("graduation")} uploading={uploadingId === "graduation"} onUpload={onUpload} onRemove={onRemove} />
    </div>
  </>
);
